import { TranslationKey } from './../../../../../types/translation/TranslationKey';
import { useMutation } from '@apollo/client';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { OnboardingData, OnboardingRoutes } from '../..';
import { ParseFileResponse, ParseFileVariables, OnboardingMutations } from '../../../../../graphql/onboarding.graphql';
import { CsvUploadType, fileNameToFileType } from '../../../../../types/csvUploadConfiguration';
import { t } from '../../../../../types/translation/Translator';
import { Button, ButtonVariant } from '../../../../components/Button/Button';
import { FileInput } from '../../../../components/File';
import FileItem from '../../../../components/File/FileItem';
import Icon, { IconType } from '../../../../icons/Icon';
import { OnboardingStep, OnboardingTitle, OnboardingSubtitle } from '../../components';
import OnboardingPaneHeader from '../../components/OnboardingPaneHeader';
import { flattenOnboardingEntities, mapParsedFileData } from '../../model/OnboardingData';
import { ToastType } from '../../../../components/Toast';
import { useToast } from '../../../../../providers/ToastProvider';
import { FileUtils } from '../../../../../types/file';

interface OnboardingStockImportPaneProps {
  data: OnboardingData;
  setData: (data: OnboardingData) => void;
}

export default function OnboardingStockImportPane({ data, setData }: OnboardingStockImportPaneProps) {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const stockLocationFiles = useMemo(() => {
    return [...data.files.entries()].filter(([id, file]) => file.type === CsvUploadType.stockLocation);
  }, [data]);

  const [upload, { loading }] = useMutation<ParseFileResponse, ParseFileVariables>(OnboardingMutations.parseFile);

  const handleFile = async (files: File[] | FileList | null) => {
    if (!files) return;
    const file = files instanceof File ? files : files[0];

    await upload({
      variables: {
        input: {
          companyId: data.company.id,
          stockLocationId: '',
          type: CsvUploadType.stockLocation,
          deleteExisting: false,
          csvFileAsBase64Data: await FileUtils.fileToBase64(file),
          fileType: fileNameToFileType(file.name),
        },
        state: flattenOnboardingEntities(data.summary),
      },
      onError: error => {
        showToast({ title: error.message, type: ToastType.error });
      },
      onCompleted: response => {
        setData(
          data.import({
            file: { name: file.name, size: file.size },
            data: mapParsedFileData(response.parseFile),
            type: CsvUploadType.stockLocation,
          }),
        );
      },
    });
  };

  return (
    <div className='grid gap-4 overflow-hidden'>
      <OnboardingPaneHeader back={() => navigate(OnboardingRoutes.company)} />

      <div className='grid gap-1'>
        <OnboardingStep />
        <OnboardingTitle text={'Add your Stock Locations'} />
        <OnboardingSubtitle
          text={
            'Define where your inventory is stored by adding stock locations. This helps you track and manage items efficiently across different sites.'
          }
        />
      </div>

      <p className='text-sm font-bold text-black'>
        Before you upload your file, make sure your file is{' '}
        <a
          className='inline-flex items-center text-blue-600'
          href={`${process.env.REACT_APP_STORAGE_URL}/ventory/stock_locations_template.xlsx`}
        >
          ready to be imported
          <span className='ml-1 inline-block'>
            <Icon type={IconType.share4} stroke={'stroke-black'} size={'h-4 w-4'} />
          </span>
        </a>
      </p>

      {!stockLocationFiles.length ? (
        <div className='grid gap-2'>
          <p className='text-sm font-medium text-gray-900'>
            Upload your file to add your stock locations to your company
          </p>
          <FileInput onFiles={handleFile} acceptedTypes={'.csv,.xlsx'} />
        </div>
      ) : null}

      {stockLocationFiles.length ? (
        <div className='flex flex-grow flex-col gap-2 overflow-auto'>
          {stockLocationFiles.map(([id, file], index) => (
            <FileItem key={id} file={file.file} onRemove={() => data.files.delete(id) && setData(data.update())} />
          ))}
        </div>
      ) : null}

      <div className='flex gap-2'>
        <Button
          text={t(TranslationKey.next).singular.label}
          onClick={() => navigate(OnboardingRoutes.stockLocationOverview)}
          disabled={!data.summary.stockLocations.size}
        />
        <Button
          text={'Instead, you can add your Stock Locations manually'}
          onClick={() => navigate(OnboardingRoutes.stockLocationAdd)}
          variant={ButtonVariant.linkPrimary}
        />
      </div>
    </div>
  );
}
